// Widget definition adapter api module — generator-facing surface for the
// dashboard-v2 Widget Studio definition catalog.
//
// CHG-2471 / ITER-905213 — Widget Studio S5 (definition catalog, READ-ONLY).
//
// WidgetDefinition is the platform-global widget-type catalog (no
// organization_id). Strictly read-only in the Studio: POST /definitions is
// seed-only, so there is NO create/mutate here. Maps the ListPage/DetailPage
// queryFn contract onto the landed widgets-admin client.

import { widgetsAdminApi } from "./widgets-admin";
import type { WidgetFamily, WidgetDefinitionStatus } from "./widgets-admin";
import type { WidgetDefinition } from "./types";

export interface ListWidgetDefinitionsParams {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  search?: string;
  filters?: Record<string, string | number | boolean | undefined>;
}

// GET /admin/widgets/definitions mapped to the ListPage contract. family +
// status select filters are wired through; search/sort are forward-promised.
export async function listWidgetDefinitions(
  params: ListWidgetDefinitionsParams = {},
): Promise<{ items: WidgetDefinition[]; total: number }> {
  const pageSize = params.pageSize ?? 50;
  const page = params.page ?? 0;
  const f = params.filters ?? {};
  const env = await widgetsAdminApi.listDefinitions({
    family: f.family as WidgetFamily | undefined,
    status: f.status as WidgetDefinitionStatus | undefined,
    limit: pageSize,
    offset: page * pageSize,
  });
  return { items: env.items, total: env.total };
}

// No GET-by-id route on the admin router — resolve the catalog entry via the
// platform-global list scan. (Definitions are a small, seeded catalog.)
export async function getWidgetDefinition(id: string): Promise<WidgetDefinition> {
  const env = await widgetsAdminApi.listDefinitions({ limit: 200, offset: 0 });
  const found = env.items.find((d) => d.id === id);
  if (!found) {
    throw new Error(`Widget definition ${id} not found`);
  }
  return found;
}

export const widgetDefinitionsApi = {
  listWidgetDefinitions,
  getWidgetDefinition,
};
